import React, {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import layers from '@rsos/components/capstone/common/Map/useDeckGL/storeLayers';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { LIGHT_THEME } from '@rsos/theme/constants';
import { useTheme } from '@rsos/theme/styled';
import {
  TEXT_COLOR_DARK,
  TEXT_COLOR_LIGHT,
  TEXT_OUTLINE_COLOR,
} from './constants';
import { hexToRgba } from './helpers';
import type {
  PureWirelessGLProps,
  DeckPickingInfo,
  DeckLayerWithId,
} from './types';
import {
  usePureWirelessCore,
  createPureWirelessMVTLayer,
  createOutdoorPointsMVTLayer,
  createGeoJSONDetailLayers,
  TOOLTIP_STYLE,
} from './usePureWirelessCore';

const usePureWirelessGL = ({ overlay, map }: PureWirelessGLProps) => {
  const themeName = useSelectedTheme();
  const theme = useTheme();
  const isDarkMode = themeName !== LIGHT_THEME;
  const callFloorHighlightColor = useMemo(
    () => hexToRgba(theme.colors.primaryBase, 255),
    [theme.colors.primaryBase],
  );
  const textColor = useMemo(
    () => (isDarkMode ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT),
    [isDarkMode],
  );
  const textOutlineColor = TEXT_OUTLINE_COLOR;
  const {
    selectedBuilding,
    isDetailMode,
    hoverInfo,
    selectedFloor,
    isVisible,
    isCardOpen,
    geoJSONData,
    isLoadingGeoJSON,
    isCallInsideBuilding,
    estimatedCallFloor,
    buildingFloorsRef,
    setHoverInfo,
    handleClick,
    handleRoomClick,
    selectedRoomFeature,
  } = usePureWirelessCore({ mapProvider: 'google' });

  // Only highlight the floor outline when viewing the estimated call floor
  const shouldHighlightFloor =
    isCallInsideBuilding && selectedFloor === estimatedCallFloor;

  const handleClickRef = useRef<(info: DeckPickingInfo) => boolean>(
    () => false,
  );
  const handleRoomClickRef = useRef<(info: DeckPickingInfo) => boolean>(
    () => false,
  );
  const handleHoverRef = useRef<(info: DeckPickingInfo) => void>(() => {
    // Intentionally empty - will be set in useEffect
  });

  useEffect(() => {
    handleClickRef.current = handleClick;
  }, [handleClick]);

  useEffect(() => {
    handleRoomClickRef.current = handleRoomClick;
  }, [handleRoomClick]);

  const handleHover = useCallback(
    (info: DeckPickingInfo) => {
      if (!isDetailMode && info.object) {
        setHoverInfo({ x: info.x, y: info.y, object: info.object });
      } else {
        setHoverInfo(null);
      }
    },
    [isDetailMode, setHoverInfo],
  );

  useEffect(() => {
    handleHoverRef.current = handleHover;
  }, [handleHover]);

  // Clear hover info when entering detail mode to prevent stale tooltip
  useEffect(() => {
    if (isDetailMode) {
      setHoverInfo(null);
    }
  }, [isDetailMode, setHoverInfo]);

  // Track Google Maps zoom for GeoJSON label/point visibility gating.
  // Only re-render when the floored zoom crosses an integer boundary,
  // so sub-pixel zoom changes during pinch/scroll don't spam the effect.
  const [currentZoom, setCurrentZoom] = useState<number | undefined>(() =>
    map?.mapInstance?.getZoom(),
  );

  useEffect(() => {
    const mapInstance = map?.mapInstance;
    if (!mapInstance) return;

    const zoom = mapInstance.getZoom();
    if (zoom !== undefined) setCurrentZoom(zoom);

    const listener = mapInstance.addListener('zoom_changed', () => {
      const z = mapInstance.getZoom();
      if (z !== undefined) {
        setCurrentZoom(prev => {
          // Only trigger re-render when floored zoom changes
          if (prev === undefined || Math.floor(z) !== Math.floor(prev))
            return z;
          return prev;
        });
      }
    });

    return () => {
      listener.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!overlay) return;

    // Clean up GeoJSON layers only - DO NOT delete MVT layer here
    // Deleting MVT layer before saving the new one causes deck.gl to call finalize()
    // on the old layer, which destroys the tile cache and forces a slow refetch
    // Use the ORIGINAL deck.gl layer IDs (not wrapped) so layer.id === layer.props.id
    const geoJSONLayerIds = [
      'pure-wireless-geojson-footprint',
      'pure-wireless-geojson-footprint-highlight',
      'pure-wireless-geojson-polygons',
      'pure-wireless-geojson-selected-room',
      'pure-wireless-geojson-points',
    ];
    geoJSONLayerIds.forEach(id => {
      layers.deleteLayer(id);
    });
    // Delete label layers for all possible floors (0-20)
    for (let floor = 0; floor <= 20; floor++) {
      layers.deleteLayer(`pure-wireless-geojson-labels-shadow-${floor}`);
      layers.deleteLayer(`pure-wireless-geojson-labels-${floor}`);
      layers.deleteLayer(`pure-wireless-geojson-point-labels-shadow-${floor}`);
      layers.deleteLayer(`pure-wireless-geojson-point-labels-${floor}`);
    }
    layers.deleteLayer('pure-wireless-geojson-labels-shadow-null');
    layers.deleteLayer('pure-wireless-geojson-labels-null');
    layers.deleteLayer('pure-wireless-geojson-point-labels-shadow-null');
    layers.deleteLayer('pure-wireless-geojson-point-labels-null');

    if (!isVisible) {
      layers.deleteLayer('PureWireless');
      layers.deleteLayer('pure-wireless-outdoor-points-dark');
      layers.deleteLayer('pure-wireless-outdoor-points-light');
      overlay.setLayers([...layers.getAll()]);
      return;
    }

    // Determine if we should show GeoJSON detail or MVT footprints
    const showGeoJSON = isDetailMode && geoJSONData && !isLoadingGeoJSON;

    // Always create MVT layer, but toggle visibility
    // When in detail mode with GeoJSON, hide MVT; otherwise show it
    const mvtLayer = createPureWirelessMVTLayer({
      buildingFloorsRef,
      onClickRef: handleClickRef,
      onHoverRef: handleHoverRef,
      visible: !showGeoJSON, // Hide when showing GeoJSON, show otherwise
    });

    (mvtLayer as DeckLayerWithId).id = 'PureWireless';
    layers.save(mvtLayer);

    // Remove stale outdoor layer from the opposite theme before saving new one
    const themeKey = isDarkMode ? 'dark' : 'light';
    const staleKey = isDarkMode ? 'light' : 'dark';
    layers.deleteLayer(`pure-wireless-outdoor-points-${staleKey}`);

    const outdoorPointsLayer = createOutdoorPointsMVTLayer({
      textColor,
      themeKey,
    });
    layers.save(outdoorPointsLayer);

    // Add GeoJSON layers when in detail mode
    if (showGeoJSON) {
      const geoJSONLayers = createGeoJSONDetailLayers({
        geoJSONData,
        selectedFloor,
        textColor,
        textOutlineColor,
        textOutlineWidth: 2,
        showPoints: true,
        onClickRef: handleRoomClickRef,
        highlightCallFloor: shouldHighlightFloor,
        callFloorHighlightColor,
        selectedRoomFeature,
        currentZoom,
      });

      // Save GeoJSON layers directly using their original deck.gl IDs.
      // IMPORTANT: Do NOT override layer.id — deck.gl's CompositeLayer (GeoJsonLayer)
      // uses this.id for sublayer management. If layer.id !== layer.props.id,
      // the internal layer diffing breaks and sublayers may fail to render.
      geoJSONLayers.forEach(layer => {
        layers.save(layer);
      });
    }

    overlay.setLayers([...layers.getAll()]);

    return () => {
      layers.deleteLayer('PureWireless');
      layers.deleteLayer('pure-wireless-outdoor-points-dark');
      layers.deleteLayer('pure-wireless-outdoor-points-light');
      // Clean up using original deck.gl layer IDs (matching how they were saved)
      const cleanupIds = [
        'pure-wireless-geojson-footprint',
        'pure-wireless-geojson-footprint-highlight',
        'pure-wireless-geojson-polygons',
        'pure-wireless-geojson-selected-room',
        'pure-wireless-geojson-points',
      ];
      cleanupIds.forEach(id => {
        layers.deleteLayer(id);
      });
      for (let floor = 0; floor <= 20; floor++) {
        layers.deleteLayer(`pure-wireless-geojson-labels-shadow-${floor}`);
        layers.deleteLayer(`pure-wireless-geojson-labels-${floor}`);
        layers.deleteLayer(
          `pure-wireless-geojson-point-labels-shadow-${floor}`,
        );
        layers.deleteLayer(`pure-wireless-geojson-point-labels-${floor}`);
      }
      layers.deleteLayer('pure-wireless-geojson-labels-shadow-null');
      layers.deleteLayer('pure-wireless-geojson-labels-null');
      layers.deleteLayer('pure-wireless-geojson-point-labels-shadow-null');
      layers.deleteLayer('pure-wireless-geojson-point-labels-null');
      overlay.setLayers([...layers.getAll()]);
    };
  }, [
    overlay,
    selectedFloor,
    isDarkMode,
    isDetailMode,
    isVisible,
    isCardOpen,
    geoJSONData,
    isLoadingGeoJSON,
    shouldHighlightFloor,
    callFloorHighlightColor,
    textColor,
    textOutlineColor,
    buildingFloorsRef,
    selectedRoomFeature,
    currentZoom,
  ]);

  return { selectedFloor, selectedBuilding, isDetailMode, hoverInfo };
};

const PureWirelessGL = React.memo(({ overlay, map }: PureWirelessGLProps) => {
  const { hoverInfo } = usePureWirelessGL({ overlay, map });

  const renderTooltip = () => {
    if (!hoverInfo) return null;

    // Get the map container to calculate proper offset
    const mapContainer = document.querySelector('.gm-style') as HTMLElement;
    let x = hoverInfo.x;
    let y = hoverInfo.y;

    if (mapContainer) {
      const rect = mapContainer.getBoundingClientRect();
      x = rect.left + hoverInfo.x + window.scrollX;
      y = rect.top + hoverInfo.y + window.scrollY;
    }

    return createPortal(
      <div
        style={{
          ...TOOLTIP_STYLE,
          left: x,
          top: y,
          transform: 'translate(10px, 10px)',
        }}
      >
        View Indoor maps
      </div>,
      document.body,
    );
  };

  return renderTooltip();
});

export default PureWirelessGL;
